'use client';

import * as React from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { DayPicker, useNavigation, useDayPicker, CaptionProps } from 'react-day-picker';
import { addYears, setMonth, setYear, getMonth, getYear } from 'date-fns';

import { cn } from '../../lib/utils';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './select';

export type CalendarProps = React.ComponentProps<typeof DayPicker>;

const MONTHS = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];

/* ─── Custom Caption with Shadcn Select ──────────────────────────────────── */
function CalendarCaption({ displayMonth }: CaptionProps) {
  const { goToMonth, nextMonth, previousMonth } = useNavigation();
  const { fromYear, toYear, fromDate, toDate } = useDayPicker();

  const currentYear = getYear(displayMonth);
  const currentMonth = getMonth(displayMonth);

  const startYear = fromYear ?? (fromDate ? getYear(fromDate) : currentYear - 100);
  const endYear = toYear ?? (toDate ? getYear(toDate) : currentYear + 10);

  const years = Array.from(
    { length: endYear - startYear + 1 },
    (_, i) => startYear + i
  );

  const isStartYear = currentYear === startYear;
  const isEndYear = currentYear === endYear;

  const startMonth = (isStartYear && fromDate) ? getMonth(fromDate) : 0;
  const endMonth = (isEndYear && toDate) ? getMonth(toDate) : 11;

  return (
    <div className="flex items-center justify-between w-full px-1">
      {/* Prev month */}
      <button
        type="button"
        onClick={() => previousMonth && goToMonth(previousMonth)}
        disabled={!previousMonth}
        className="w-7 h-7 flex items-center justify-center rounded-lg text-slate-400 hover:text-slate-700 dark:hover:text-slate-200 hover:bg-slate-100 dark:hover:bg-white/[0.06] transition-colors disabled:opacity-30 disabled:pointer-events-none"
        aria-label="Go to previous month"
      >
        <ChevronLeft size={14} strokeWidth={2.5} />
      </button>

      {/* Month + Year selects */}
      <div className="flex items-center gap-1.5">
        {/* Month Select */}
        <Select
          value={String(currentMonth)}
          onValueChange={(val) => goToMonth(setMonth(displayMonth, Number(val)))}
        >
          <SelectTrigger className="h-7 text-[12.5px] font-semibold px-2 py-0 min-w-[100px]">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {MONTHS.map((name, i) => (
              <SelectItem key={name} value={String(i)} disabled={i < startMonth || i > endMonth}>
                {name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        {/* Year Select */}
        <Select
          value={String(currentYear)}
          onValueChange={(val) => goToMonth(setYear(displayMonth, Number(val)))}
        >
          <SelectTrigger className="h-7 text-[12.5px] font-semibold px-2 py-0 min-w-[72px]">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {years.map((y) => (
              <SelectItem key={y} value={String(y)}>
                {y}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Next month */}
      <button
        type="button"
        onClick={() => nextMonth && goToMonth(nextMonth)}
        disabled={!nextMonth}
        className="w-7 h-7 flex items-center justify-center rounded-lg text-slate-400 hover:text-slate-700 dark:hover:text-slate-200 hover:bg-slate-100 dark:hover:bg-white/[0.06] transition-colors disabled:opacity-30 disabled:pointer-events-none"
        aria-label="Go to next month"
      >
        <ChevronRight size={14} strokeWidth={2.5} />
      </button>
    </div>
  );
}

/* ─── Calendar ───────────────────────────────────────────────────────────── */
function Calendar({
  className,
  classNames,
  showOutsideDays = true,
  ...props
}: CalendarProps) {
  return (
    <DayPicker
      showOutsideDays={showOutsideDays}
      className={cn('p-3', className)}
      classNames={{
        months: 'flex flex-col gap-4',
        month: 'flex flex-col gap-3',
        caption: 'flex justify-center pt-0.5 relative items-center w-full',
        caption_label: 'hidden',
        nav: 'hidden',                 // hidden — our custom Caption handles nav
        table: 'w-full border-collapse',
        head_row: 'flex',
        head_cell:
          'text-[10px] font-bold text-slate-400 dark:text-slate-500 uppercase tracking-wider w-9 h-7 flex items-center justify-center select-none',
        row: 'flex w-full mt-1',
        cell: cn(
          'relative p-0 text-center focus-within:relative focus-within:z-20',
          '[&:has([aria-selected])]:bg-transparent',
          '[&:has([aria-selected].day-outside)]:bg-transparent'
        ),
        day: cn(
          'h-9 w-9 p-0 font-medium text-[12.5px] rounded-lg',
          'flex items-center justify-center cursor-pointer select-none',
          'transition-all duration-100',
          'text-slate-700 dark:text-slate-200',
          'hover:bg-slate-100 dark:hover:bg-white/[0.06]',
          'focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500/30'
        ),
        day_selected: cn(
          'bg-brand-600 text-white font-bold',
          'shadow-sm shadow-brand-600/25',
          'hover:bg-brand-700 hover:text-white',
          'focus:bg-brand-600 focus:text-white'
        ),
        day_today: cn(
          'ring-1 ring-brand-400 dark:ring-brand-500',
          'text-brand-600 dark:text-brand-400 font-semibold',
          'bg-brand-50 dark:bg-brand-500/10'
        ),
        day_outside: cn(
          'day-outside text-slate-300 dark:text-slate-700 opacity-60',
          'aria-selected:text-white aria-selected:opacity-90'
        ),
        day_disabled:
          'text-slate-300 dark:text-slate-700 cursor-not-allowed opacity-50 hover:bg-transparent dark:hover:bg-transparent',
        day_range_middle:
          'aria-selected:bg-brand-50 dark:aria-selected:bg-brand-500/10 aria-selected:text-brand-600 aria-selected:rounded-none',
        day_hidden: 'invisible',
        ...classNames,
      }}
      components={{
        Caption: CalendarCaption,
        IconLeft: () => <ChevronLeft size={14} strokeWidth={2.5} />,
        IconRight: () => <ChevronRight size={14} strokeWidth={2.5} />,
      }}
      {...props}
    />
  );
}

Calendar.displayName = 'Calendar';

export { Calendar };
